import * as THREE from "three";
import { EventBus } from "../EventBus.js";
import { WorldStore } from "../../store/WorldStore.js";
import { doorTextureUrl } from "../adventure/ActiveAdventure.js";
import { INTERACTIVE_LAYER } from "./Raycaster.js";
import type { GridMap } from "../grid/GridMap.js";
import type { Position } from "../types/grid.js";
import { getClosedDoorTexture, getOpenDoorTexture } from "./doorTextures.js";

/**
 * Door rendering + state wiring (W17).
 *
 * Places a wall-aligned plane per registered door, oriented across the
 * corridor the door sits in. Subscribes to `world:door_state_changed`
 * so `WorldStore.setDoorOpen` is the single source of truth for visuals
 * and collision (`GridMap.isPassable`) alike.
 *
 * The plane's userData carries `{ instanceId: doorId, kind: "door",
 * cellX, cellZ }` so `InteractionDispatcher` can identify a door click
 * and run an adjacency check against the party's current cell.
 */

const CELL_SIZE = 1;
const DOOR_HEIGHT = 0.9;
const DOOR_WIDTH = 0.85;
const DOOR_Y = DOOR_HEIGHT / 2;

type Orientation = "northSouth" | "eastWest";

// ── Authored door-skin probe ─────────────────────────────────────────────────
// Adventures may ship `world/doors/{closed,open}.png`; a 404 keeps the
// procedural canvas doors. Same cache/inflight pattern as PropManager's
// loadAuthoredImage — "failed" is cached so each URL is probed once per
// session (the cache key is the full per-adventure URL).

type AuthoredImageState = HTMLImageElement | "failed";
const authoredDoorImages = new Map<string, AuthoredImageState>();
const pendingDoorLoads = new Map<string, Promise<HTMLImageElement | null>>();

function loadAuthoredDoorImage(url: string): Promise<HTMLImageElement | null> {
  const cached = authoredDoorImages.get(url);
  if (cached === "failed") return Promise.resolve(null);
  if (cached) return Promise.resolve(cached);
  const inflight = pendingDoorLoads.get(url);
  if (inflight) return inflight;

  const p = new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  }).then((img) => {
    pendingDoorLoads.delete(url);
    authoredDoorImages.set(url, img ?? "failed");
    return img;
  });
  pendingDoorLoads.set(url, p);
  return p;
}

function authoredDoorTexture(img: HTMLImageElement): THREE.Texture {
  const tex = new THREE.Texture(img);
  tex.needsUpdate = true;
  // Authored PNGs are sRGB — tag them or the renderer gamma-lifts on output.
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.LinearFilter;
  return tex;
}

export class DoorRenderer {
  private readonly meshes = new Map<string, THREE.Mesh>();
  private readonly unsub: () => void;

  constructor(
    private readonly scene: THREE.Scene,
    gridMap: GridMap,
    doorCells: ReadonlyArray<{ position: Position; doorId: string }>,
  ) {
    // PBR (W20) so doors respond to scene lighting like the surrounding walls.
    const matClosed = new THREE.MeshStandardMaterial({
      map: getClosedDoorTexture(),
      transparent: true,
      side: THREE.DoubleSide,
      roughness: 0.85,
      metalness: 0.0,
    });
    const matOpen = new THREE.MeshStandardMaterial({
      map: getOpenDoorTexture(),
      transparent: true,
      side: THREE.DoubleSide,
      roughness: 0.85,
      metalness: 0.0,
    });

    // Probe the adventure's authored door skin; on hit, swap the shared
    // material's map in place so every mesh (and both states) picks it up.
    // A miss leaves the procedural canvas texture.
    for (const state of ["closed", "open"] as const) {
      void loadAuthoredDoorImage(doorTextureUrl(state)).then((img) => {
        if (!img) return;
        const mat = state === "closed" ? matClosed : matOpen;
        mat.map = authoredDoorTexture(img);
        mat.needsUpdate = true;
      });
    }

    for (const { position, doorId } of doorCells) {
      const cell = gridMap.getCell(position);
      if (!cell) continue;
      const orientation = inferOrientation(cell.edges);

      const geo = new THREE.PlaneGeometry(DOOR_WIDTH, DOOR_HEIGHT);
      const isOpen = WorldStore.isDoorOpen(doorId);
      const mesh = new THREE.Mesh(geo, isOpen ? matOpen : matClosed);

      const wx = position.x * CELL_SIZE;
      const wz = position.z * CELL_SIZE;
      mesh.position.set(wx, DOOR_Y, wz);
      if (orientation === "eastWest") {
        mesh.rotation.y = Math.PI / 2;
      }

      mesh.userData = {
        instanceId: doorId,
        kind: "door",
        cellX: position.x,
        cellZ: position.z,
      };
      mesh.layers.set(INTERACTIVE_LAYER);

      this.scene.add(mesh);
      this.meshes.set(doorId, mesh);
    }

    this.unsub = EventBus.on("world:door_state_changed", ({ doorId, open }) => {
      const mesh = this.meshes.get(doorId);
      if (!mesh) return;
      mesh.material = open ? matOpen : matClosed;
    });
  }

  dispose(): void {
    this.unsub();
    for (const mesh of this.meshes.values()) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
    }
    this.meshes.clear();
  }
}

/**
 * Infer door plane orientation from the surrounding walls of the door
 * cell. A standard corridor door cell has walls on the two edges
 * perpendicular to movement and open edges along the axis of movement.
 * The door plane sits across the movement axis.
 *
 * Ambiguous or non-corridor geometry falls back to north-south.
 */
export function inferOrientation(edges: {
  north: boolean;
  south: boolean;
  east: boolean;
  west: boolean;
}): Orientation {
  const openNS = !edges.north && !edges.south;
  const openEW = !edges.east && !edges.west;
  if (openNS && !openEW) return "northSouth";
  if (openEW && !openNS) return "eastWest";
  // Ambiguous: pick based on which pair has more walls.
  const wallsNS = (edges.north ? 1 : 0) + (edges.south ? 1 : 0);
  const wallsEW = (edges.east ? 1 : 0) + (edges.west ? 1 : 0);
  return wallsEW > wallsNS ? "northSouth" : "eastWest";
}
